import { motion } from 'framer-motion';
import { useEffect } from 'react';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';
import FloatingButtons from '../home/components/FloatingButtons';
import { useInView } from '../home/hooks/useInView';

const LUnion = () => {
  const [heroRef, heroInView] = useInView({ threshold: 0.1 });
  const [servicesRef, servicesInView] = useInView({ threshold: 0.1 });
  const [testimonialsRef, testimonialsInView] = useInView({ threshold: 0.1 });

  useEffect(() => {
    document.title = 'Réparation de fenêtres à L\'Union – Intervention rapide 24h/24 | Vitrerie 31';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Expert réparation fenêtres à L\'Union (31240). Intervention sous 2h pour fenêtres PVC, bois, alu. Devis gratuit. ☎ 05 54 54 25 45');
    }
  }, []);

  const services = [
    { icon: 'ri-tools-line', title: 'Réparation Fenêtres PVC', desc: 'Joints, mécanismes, vitrage' },
    { icon: 'ri-hammer-line', title: 'Fenêtres Bois', desc: 'Rénovation et entretien' },
    { icon: 'ri-window-line', title: 'Fenêtres Aluminium', desc: 'Réparation et remplacement' },
    { icon: 'ri-shield-check-line', title: 'Vitrerie d\'urgence', desc: 'Intervention 24h/24' }
  ];

  const testimonials = [
    {
      name: 'Sophie Martin',
      location: 'Quartier Borderouge',
      text: 'Fenêtre qui ne s\'ouvrait plus. Intervention très rapide et technicien compétent. Problème résolu en 1h !',
      rating: 5
    },
    {
      name: 'Laurent Mercier',
      location: 'Centre-ville L\'Union',
      text: 'Remplacement de vitrage cassé. Service impeccable, prix correct. Je recommande sans hésiter !',
      rating: 5
    },
    {
      name: 'Isabelle Rousseau',
      location: 'Quartier Croix Daurade',
      text: 'Problème d\'isolation phonique résolu. Équipe professionnelle et à l\'écoute. Excellent travail !',
      rating: 5
    }
  ];

  const problemesTypiques = [
    { icon: 'ri-volume-up-line', title: 'Isolation phonique', desc: 'Proximité axes routiers' },
    { icon: 'ri-water-flash-line', title: 'Condensation', desc: 'Humidité et double vitrage' },
    { icon: 'ri-lock-unlock-line', title: 'Mécanismes usés', desc: 'Ouverture et fermeture' },
    { icon: 'ri-wind-line', title: 'Courants d\'air', desc: 'Joints détériorés' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero Section */}
      <section ref={heroRef} className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-teal-50 via-white to-orange-50"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/5 via-transparent to-transparent"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <i className="ri-map-pin-line"></i>
              <span>L'Union - Haute-Garonne (31240)</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Réparation de fenêtres à <strong className="text-teal-600">L'Union</strong>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Intervention rapide <strong>sous 2h</strong> pour tous vos problèmes de fenêtres
            </p>

            <div className="flex flex-wrap justify-center gap-4 mb-12">
              <a
                href="tel:0554542545"
                className="inline-flex items-center gap-2 bg-teal-600 text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-teal-700 transition-all shadow-lg hover:scale-105 whitespace-nowrap cursor-pointer active:scale-95"
              >
                <i className="ri-phone-line text-xl"></i>
                05 54 54 25 45
              </a>
              <a
                href="#contact"
                className="inline-flex items-center gap-2 bg-white text-teal-600 px-8 py-4 rounded-full font-semibold text-lg hover:bg-gray-50 transition-all shadow-lg hover:scale-105 whitespace-nowrap cursor-pointer border-2 border-teal-600 active:scale-95"
              >
                <i className="ri-message-line text-xl"></i>
                Devis gratuit
              </a>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              <div className="bg-white rounded-lg p-6 shadow-lg">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-time-line text-2xl text-teal-600"></i>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Intervention rapide</h3>
                <p className="text-gray-600 text-sm">Sous 2h à L'Union</p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-lg">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-map-pin-2-line text-2xl text-orange-600"></i>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">À 8 km de Toulouse</h3>
                <p className="text-gray-600 text-sm">Tous quartiers desservis</p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-lg">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-24-hours-line text-2xl text-teal-600"></i>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Urgences 24h/24</h3>
                <p className="text-gray-600 text-sm">7 jours sur 7</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section ref={servicesRef} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={servicesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Nos services à <strong className="text-teal-600">L'Union</strong>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Experts en réparation de fenêtres pour tous les quartiers
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={servicesInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gradient-to-br from-teal-50 to-orange-50 rounded-lg p-6 hover:shadow-xl transition-all cursor-pointer group"
              >
                <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center mb-4 group-hover:bg-teal-500 transition-colors">
                  <i className={`${service.icon} text-3xl text-teal-600 group-hover:text-white transition-colors`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{service.title}</h3>
                <p className="text-gray-600">{service.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Problèmes Typiques */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Problèmes fréquents à <strong className="text-teal-600">L'Union</strong>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Nous connaissons les spécificités de votre commune
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {problemesTypiques.map((probleme, index) => (
              <div
                key={index}
                className="bg-white rounded-lg p-6 shadow-lg hover:shadow-xl transition-all"
              >
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                  <i className={`${probleme.icon} text-2xl text-orange-600`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{probleme.title}</h3>
                <p className="text-gray-600 text-sm">{probleme.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 bg-gradient-to-r from-teal-500 to-orange-500 rounded-lg p-8 text-white text-center">
            <h3 className="text-2xl font-bold mb-4">Pourquoi ces problèmes à L'Union ?</h3>
            <p className="text-lg mb-6 opacity-90 max-w-3xl mx-auto">
              Située à <strong>8 km au nord-est de Toulouse</strong>, L'Union est une commune résidentielle dynamique traversée par des <strong>axes routiers importants</strong> (RN88, RD888). Les quartiers <strong>Borderouge, Centre-ville et Croix Daurade</strong> nécessitent souvent une <strong>isolation phonique renforcée</strong> pour les fenêtres. La proximité de Toulouse entraîne également des problèmes classiques de condensation et d'usure des mécanismes.
            </p>
          </div>
        </div>
      </section>

      {/* Témoignages Clients */}
      <section ref={testimonialsRef} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={testimonialsInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Avis de nos clients à <strong className="text-teal-600">L'Union</strong>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Ils nous ont fait confiance pour leurs fenêtres
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={testimonialsInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gradient-to-br from-teal-50 to-orange-50 rounded-lg p-6 shadow-lg"
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-500 text-xl"></i>
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">"{testimonial.text}"</p>
                <div className="border-t border-gray-200 pt-4">
                  <p className="font-bold text-gray-900">{testimonial.name}</p>
                  <p className="text-sm text-gray-600 flex items-center gap-1">
                    <i className="ri-map-pin-line"></i>
                    {testimonial.location}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* NOUVEAU BLOC 1 - Isolation Phonique Axes Routiers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={isInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="relative"
        >
          <div className="relative w-full h-[500px] rounded-2xl overflow-hidden shadow-2xl">
            <img
              src="https://public.readdy.ai/ai/img_res/be5150409d49853cd74b03bc08e935f6.jpg"
              alt="Isolation phonique fenêtres L'Union axes routiers"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-teal-900/60 via-transparent to-transparent"></div>
            
            <div className="absolute bottom-6 left-6 bg-white rounded-xl shadow-xl p-4">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                  <i className="ri-volume-mute-line text-teal-600 text-2xl"></i>
                </div>
                <div>
                  <p className="text-lg font-bold text-gray-900">Anti-Bruit</p>
                  <p className="text-sm text-gray-600">Isolation acoustique</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 30 }}
          animate={isInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <h3 className="text-3xl font-bold text-gray-900 mb-6">
            <strong className="text-teal-600">Isolation Phonique Axes Routiers</strong> à L'Union
          </h3>
          <p className="text-gray-600 mb-4 leading-relaxed">
            L'Union est traversée par plusieurs <strong>axes routiers importants</strong> (RD112, Avenue de Launaguet) générant des nuisances sonores importantes. Notre expertise en <strong>isolation phonique de fenêtres à L'Union</strong> vous permet de retrouver le calme et la tranquillité dans votre habitation, même en bordure de route.
          </p>
          <p className="text-gray-600 mb-6 leading-relaxed">
            Nous installons des <strong>fenêtres anti-bruit haute performance</strong> avec vitrage acoustique asymétrique, capables de réduire les nuisances sonores routières jusqu'à 42 décibels. Nos solutions incluent le <strong>double vitrage phonique 10/16/4</strong>, le <strong>triple vitrage acoustique</strong> et les <strong>joints d'étanchéité renforcés</strong> pour une isolation optimale à L'Union.
          </p>

          <div className="bg-gradient-to-r from-teal-50 to-blue-50 rounded-xl p-6 mb-6">
            <h4 className="font-bold text-gray-900 mb-4 flex items-center">
              <i className="ri-roadster-line text-teal-600 mr-2 text-xl"></i>
              Réduction Bruit Routier
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white rounded-lg p-4 text-center">
                <p className="text-3xl font-bold text-teal-600 mb-1">-42dB</p>
                <p className="text-sm text-gray-600">Bruit circulation</p>
              </div>
              <div className="bg-white rounded-lg p-4 text-center">
                <p className="text-3xl font-bold text-orange-600 mb-1">-35dB</p>
                <p className="text-sm text-gray-600">Bruit motos/camions</p>
              </div>
            </div>
          </div>

          <div className="space-y-3 mb-6">
            <div className="flex items-start">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                <i className="ri-car-line text-2xl text-teal-600"></i>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Vitrage Asymétrique Acoustique</h4>
                <p className="text-gray-600 text-sm">Épaisseurs différentes (10mm/4mm) pour casser les fréquences sonores</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                <i className="ri-home-smile-2-line text-2xl text-orange-600"></i>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Confort Acoustique Garanti</h4>
                <p className="text-gray-600 text-sm">Sommeil paisible, concentration améliorée, qualité de vie retrouvée</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                <i className="ri-temp-cold-line text-2xl text-teal-600"></i>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Double Performance</h4>
                <p className="text-gray-600 text-sm">Isolation phonique ET thermique pour un confort total</p>
              </div>
            </div>
          </div>

          <a
            href="#contact"
            className="inline-block bg-gradient-to-r from-teal-500 to-teal-600 text-white px-8 py-4 rounded-full font-semibold hover:from-teal-600 hover:to-teal-700 transition-all shadow-lg hover:shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-phone-line mr-2"></i>
            Devis Isolation Phonique
          </a>
        </motion.div>
      </div>

      {/* NOUVEAU BLOC 2 - Menuiseries PVC Modernes */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={isInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="order-2 lg:order-1"
        >
          <h3 className="text-3xl font-bold text-gray-900 mb-6">
            <strong className="text-orange-600">Fenêtres PVC Haute Performance</strong> à L'Union
          </h3>
          <p className="text-gray-600 mb-4 leading-relaxed">
            Les <strong>fenêtres PVC</strong> sont la solution idéale pour allier performance, durabilité et esthétique à L'Union. Notre gamme de <strong>menuiseries PVC haute performance</strong> offre une excellente isolation thermique et phonique, un entretien minimal et une longévité exceptionnelle de plus de 40 ans.
          </p>
          <p className="text-gray-600 mb-6 leading-relaxed">
            Nous proposons l'<strong>installation de fenêtres PVC à L'Union</strong> avec <strong>double ou triple vitrage</strong>, <strong>profilés multi-chambres</strong> pour une isolation renforcée, <strong>large choix de coloris</strong> (blanc, gris anthracite, imitation bois) et <strong>quincaillerie haute sécurité</strong>. Nos fenêtres PVC sont certifiées Cekal et NF pour garantir leur qualité.
          </p>

          <div className="bg-gradient-to-r from-orange-50 to-teal-50 rounded-xl p-6 mb-6">
            <h4 className="font-bold text-gray-900 mb-4 flex items-center">
              <i className="ri-star-line text-orange-600 mr-2 text-xl"></i>
              Avantages Fenêtres PVC
            </h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white rounded-lg p-3 text-center">
                <i className="ri-temp-cold-line text-2xl text-teal-600 mb-1"></i>
                <p className="text-xs text-gray-600 font-semibold">Isolation thermique</p>
              </div>
              <div className="bg-white rounded-lg p-3 text-center">
                <i className="ri-volume-mute-line text-2xl text-teal-600 mb-1"></i>
                <p className="text-xs text-gray-600 font-semibold">Isolation phonique</p>
              </div>
              <div className="bg-white rounded-lg p-3 text-center">
                <i className="ri-time-line text-2xl text-orange-600 mb-1"></i>
                <p className="text-xs text-gray-600 font-semibold">Durée 40+ ans</p>
              </div>
              <div className="bg-white rounded-lg p-3 text-center">
                <i className="ri-brush-line text-2xl text-orange-600 mb-1"></i>
                <p className="text-xs text-gray-600 font-semibold">Sans entretien</p>
              </div>
            </div>
          </div>

          <div className="space-y-3 mb-6">
            <div className="flex items-center text-gray-700">
              <i className="ri-check-line text-orange-600 mr-3 text-xl"></i>
              <span>Profilés 5 à 7 chambres pour isolation maximale</span>
            </div>
            <div className="flex items-center text-gray-700">
              <i className="ri-check-line text-orange-600 mr-3 text-xl"></i>
              <span>Coefficient Uw jusqu'à 0,8 W/m².K</span>
            </div>
            <div className="flex items-center text-gray-700">
              <i className="ri-check-line text-orange-600 mr-3 text-xl"></i>
              <span>Garantie 10 ans sur profilés et quincaillerie</span>
            </div>
            <div className="flex items-center text-gray-700">
              <i className="ri-check-line text-orange-600 mr-3 text-xl"></i>
              <span>Éligible MaPrimeRénov' et aides financières</span>
            </div>
          </div>

          <a
            href="#contact"
            className="inline-block bg-gradient-to-r from-orange-500 to-orange-600 text-white px-8 py-4 rounded-full font-semibold hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg hover:shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-phone-line mr-2"></i>
            Devis Fenêtres PVC Gratuit
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 30 }}
          animate={isInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="relative order-1 lg:order-2"
        >
          <div className="relative w-full h-[500px] rounded-2xl overflow-hidden shadow-2xl">
            <img
              src="https://readdy.ai/api/search-image?query=modern%20high%20quality%20white%20pvc%20window%20with%20multi%20chamber%20profile%20showing%20cross%20section%20detail%20of%20insulation%20technology%20double%20glazing%20contemporary%20design%20installed%20in%20beautiful%20home%20demonstrating%20energy%20efficiency%20durability%20low%20maintenance%20simple%20clean%20background&width=800&height=600&seq=lunion-pvc-window-v1&orientation=landscape"
              alt="Fenêtres PVC haute performance L'Union"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-orange-900/60 via-transparent to-transparent"></div>
            
            <div className="absolute bottom-6 right-6 bg-white rounded-xl shadow-xl p-4">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                  <i className="ri-window-line text-orange-600 text-2xl"></i>
                </div>
                <div>
                  <p className="text-lg font-bold text-gray-900">PVC Premium</p>
                  <p className="text-sm text-gray-600">Haute qualité</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Quartiers desservis */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Tous les quartiers de <strong className="text-teal-600">L'Union</strong>
            </h2>
            <p className="text-lg text-gray-600">Intervention rapide dans toute la commune</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {['Centre-ville', 'Borderouge', 'Croix Daurade', 'Montberon', 'Casselardit', 'Rangueil', 'Saouzelong', 'Montaudran'].map((quartier, index) => (
              <div
                key={index}
                className="bg-white rounded-lg p-4 shadow-md hover:shadow-lg transition-all text-center"
              >
                <i className="ri-map-pin-2-fill text-2xl text-teal-600 mb-2"></i>
                <p className="font-semibold text-gray-900">{quartier}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-teal-500 to-orange-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Besoin d'une réparation de fenêtre à L'Union ?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Intervention rapide sous 2h - Devis gratuit - Urgences 24h/24
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="tel:0554542545"
              className="inline-flex items-center gap-2 bg-white text-teal-600 px-8 py-4 rounded-full font-semibold text-lg hover:bg-gray-50 transition-all shadow-lg hover:scale-105 whitespace-nowrap cursor-pointer active:scale-95"
            >
              <i className="ri-phone-line text-xl"></i>
              Appelez maintenant
            </a>
            <a
              href="/#contact"
              className="inline-flex items-center gap-2 bg-transparent border-2 border-white text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-white/10 transition-all shadow-lg hover:scale-105 whitespace-nowrap cursor-pointer active:scale-95"
            >
              <i className="ri-message-line text-xl"></i>
              Demander un devis
            </a>
          </div>
        </div>
      </section>

      {/* Liens vers autres villes */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            Nous intervenons aussi dans les villes voisines
          </h3>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="/" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Toulouse</a>
            <span className="text-gray-400">•</span>
            <a href="/colomiers" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Colomiers</a>
            <span className="text-gray-400">•</span>
            <a href="/blagnac" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Blagnac</a>
            <span className="text-gray-400">•</span>
            <a href="/tournefeuille" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Tournefeuille</a>
            <span className="text-gray-400">•</span>
            <a href="/muret" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Muret</a>
            <span className="text-gray-400">•</span>
            <a href="/balma" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Balma</a>
            <span className="text-gray-400">•</span>
            <a href="/ramonville" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Ramonville</a>
            <span className="text-gray-400">•</span>
            <a href="/cugnaux" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Cugnaux</a>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingButtons />
    </div>
  );
};

export default LUnion;